import { getContrastColor, hexToRgba } from '../../../utils/colors.helper'
import React from 'react'
import { createRoot } from 'react-dom/client'
import GutterBody from './GutterBody'
import { LANGUAGES } from '../../../constants/languages'

export const mapFormatToMIME = format => {
    const languageMIMEMap = {}
    Object.values(LANGUAGES).forEach(LANGUAGE => (languageMIMEMap[LANGUAGE.value] = LANGUAGE.mimeValue))
    return languageMIMEMap[format] || format
}

//mapping and using dynamic require path(require(MIMEImportPathMap[language])) will not work
export const importLanguagePackage = languageMime => {
    switch (languageMime) {
        case LANGUAGES.PYTHON.mimeValue:
            require('codemirror/mode/python/python')
            break
        case LANGUAGES.C.mimeValue:
        case LANGUAGES.CPP.mimeValue:
        case LANGUAGES.JAVA.mimeValue:
            require('codemirror/mode/clike/clike')
            break
        case LANGUAGES.MATLAB.mimeValue:
            require('codemirror/mode/octave/octave')
            break
        case LANGUAGES.SQL.mimeValue:
            require('codemirror/mode/sql/sql')
            break
    }
}

export const buildPatternGutter = (
    editor,
    color,
    id,
    startRow,
    endRow,
    hideId,
    tooltip,
    onGutterClick,
    isSelected,
    onSelectPattern,
) => {
    const handleGutterClick = event => {
        if (event.button !== 0) return
        event.preventDefault()
        event.stopPropagation()
        onGutterClick(id, startRow, endRow)
    }

    let handleSelectPattern
    if (onSelectPattern)
        handleSelectPattern = () => {
            onSelectPattern({
                motifId: id,
                startRow: startRow,
                endRow: endRow,
            })
        }

    let patternHeightInPx = 0
    for (let i = startRow - 1; i <= endRow - 1; i++) {
        // '-1' because counting in codemirror starts from 0
        patternHeightInPx += editor.getLineHandle(i)?.height
    }

    const marker = document.createElement(onGutterClick ? 'button' : 'div')
    if (onGutterClick) marker.addEventListener('mouseup', handleGutterClick, true)
    marker.classList.add('gutter-marker')
    marker.ariaLabel = `pattern ${id}`
    if (tooltip) marker.title = tooltip
    
    // Create root and render using React 18 API
    const root = createRoot(marker)
    root.render(
        <GutterBody id={hideId ? null : id} isSelected={isSelected} onPatternSelect={handleSelectPattern} />
    )
    
    marker.style.backgroundColor = `${color}`
    marker.style.border = '0'
    marker.style.padding = '0'
    marker.style.height = `${patternHeightInPx}px`
    marker.style.width = '100%'
    marker.style.color = getContrastColor(color)
    marker.style.borderRadius = '4px'
    marker.style.textAlign = 'center'
    marker.style.cursor = onGutterClick ? 'pointer' : 'unset'
    marker.onclick = onGutterClick ? handleGutterClick : null
    return marker
}

const _termsToSkip = ['(', ')']

export const isTermValid = text => text.trim() && !_termsToSkip.some(term => text.includes(term))

export const incrementTermsLine = (terms, numberToIncrement) =>
    terms.map(term => ({
        text: term.text,
        type: term.type,
        cords: {
            anchor: {
                line: term.cords.anchor.line + numberToIncrement,
                ch: term.cords.anchor.ch,
            },
            head: {
                line: term.cords.head.line + numberToIncrement,
                ch: term.cords.head.ch,
            },
        },
    }))

export const getStringCoordinates = (code, cursorPosition) => {
    const lines = code.split('\n')
    const line = lines[cursorPosition.line]
    const regex = /"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/g
    let match

    let currentPosition = 0
    while ((match = regex.exec(line)) !== null) {
        const stringPosition = match.index
        const stringEndPosition = stringPosition + match[0].length - 1

        if (
            cursorPosition.ch >= stringPosition - currentPosition &&
            cursorPosition.ch <= stringEndPosition - currentPosition
        ) {
            return {
                anchor: {
                    line: cursorPosition.line,
                    ch: stringPosition + currentPosition,
                },
                head: {
                    line: cursorPosition.line,
                    ch: stringEndPosition + currentPosition + 1,
                },
            }
        }

        currentPosition = stringEndPosition + 1
    }

    return null
}

const allSnippetsIntersect = (...snippets) =>
    Math.max(...snippets.map(r => r.start)) <= Math.min(...snippets.map(r => r.end))

export const removeIntersections = array =>
    array.reduce((acc, snippet) => {
        let snippetToRemain = snippet
        array.map(snippetToCompare => {
            if (allSnippetsIntersect(snippet, snippetToCompare)) {
                snippetToRemain = snippetToCompare
            }
        })
        const snippetWasNotPushedYet = !acc.some(
            snippetToCompare =>
                snippetToCompare.start === snippetToRemain.start && snippetToCompare.end === snippetToRemain.end,
        )
        if (snippetWasNotPushedYet) acc.push(snippetToRemain)
        return acc
    }, [])

export const countWordsAndGetExcess = (text, wordsLimit = 0) => {
    const words = text.split(' ')
    const wordCount = words.length
    const excessStart = wordsLimit < wordCount ? words.slice(0, wordsLimit).join(' ').length : null
    return { wordCount, excessStart }
}

export const applyHighlights = (editor, highlights) => {
    editor.getAllMarks().forEach(mark => (mark.className === 'highlight' ? mark.clear() : null))
    if (Array.isArray(highlights))
        highlights.forEach(highlight => {
            const { start, end, color, textColor, opacity } = highlight
            const from = editor.posFromIndex(start)
            const to = editor.posFromIndex(end)
            const rgbaColor = hexToRgba(color, opacity)
            editor.markText(from, to, {
                css: `background-color: ${rgbaColor}; color: ${textColor || getContrastColor(rgbaColor)};`,
                className: 'highlight',
            })
        })
}
